import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";

export type NavigationLink = {
  key: string;
  label: string;
  onSelect: () => void;
};

type NavigationCardProps = {
  icon: ReactNode;
  title: string;
  /** Page principale de la rubrique, ouverte par un clic sur le haut de la carte. */
  onTitleSelect?: () => void;
  links: NavigationLink[];
  /** Contenu entre le titre et les liens (chiffres de « Mes données »). */
  children?: ReactNode;
  footer?: ReactNode;
};

/**
 * Carte de rubrique de l'accueil (Mes données, Site internet, Contacts).
 * Toujours opaque : elle reste lisible quel que soit le fond choisi.
 * Avec `onTitleSelect`, tout le haut de la carte (titre et espace libre
 * jusqu'aux liens) mène à la page principale de la rubrique.
 */
export const NavigationCard = ({
  icon,
  title,
  onTitleSelect,
  links,
  children,
  footer,
}: NavigationCardProps) => (
  <section className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-sm">
    <header
      className={cn(
        "relative flex-1 p-4 pb-5",
        onTitleSelect && "group transition-colors hover:bg-slate-50",
      )}
    >
      <h2 className="flex min-w-0 items-center gap-3 text-xl font-bold uppercase tracking-wide text-primary">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-50 text-primary">
          {icon}
        </span>
        {onTitleSelect ? (
          // Le ::after étire la zone de clic à tout l'en-tête.
          <button
            type="button"
            onClick={onTitleSelect}
            className="flex min-w-0 items-center gap-2 rounded-md uppercase after:absolute after:inset-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <span className="min-w-0 truncate">{title}</span>
            <ArrowRight
              className="size-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
              aria-hidden
            />
          </button>
        ) : (
          <span className="min-w-0 truncate">{title}</span>
        )}
      </h2>
    </header>

    {children && <div className="px-4 pb-5">{children}</div>}

    {/* Hauteur réservée comme sur la maquette : les listes des 3 cartes démarrent à la même hauteur. */}
    <div className="flex flex-col gap-6 px-4 pb-4 lg:min-h-[212px]">
      <ul className="flex list-none flex-col">
        {links.map((link) => (
          <li key={link.key} className="border-t border-slate-200">
            <button
              type="button"
              onClick={link.onSelect}
              className="group flex w-full items-center justify-between gap-3 py-2.5 text-left text-sm"
            >
              <span className="min-w-0 truncate group-hover:text-primary">
                {link.label}
              </span>
              <ArrowRight
                className="size-4 shrink-0 text-slate-500 transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                aria-hidden
              />
            </button>
          </li>
        ))}
      </ul>
      {footer}
    </div>
  </section>
);
